import { useQuery } from '@tanstack/react-query'
import React, { Suspense } from 'react'
import { Actions } from '~/actions'
import { useTranslations } from '~/components/Locale'
import { CollectionDocument, ProductsDocument } from '~/gql'
import type { CollectionSection as Settings } from '~/types'
import { cn, getCurrentPageProductId, gidToId, productUrl, shopifyFetch, useDispatch } from '~/utils'
import { ProductCard } from './ProductCard'
import { sortProducts } from './productUtils'
import { List, Section } from './Section'

export interface CollectionProps {
	section: Settings
}

const CollectionSection: React.FC<CollectionProps> = ({ section }) => {
	const dispatch = useDispatch()
	const t = useTranslations('react')
	const res = useQuery(['products'], async () => shopifyFetch(ProductsDocument, {})).data
	const collection = useQuery(['collection', section.settings.collection], async () =>
		shopifyFetch(CollectionDocument, { handle: section.settings.collection })
	).data?.data

	const productIds = collection?.products?.map(p => p.id)?.filter(id => gidToId(id) !== getCurrentPageProductId())

	const products = res?.data?.filter(p => productIds?.includes(p.id)).sort(sortProducts(section.settings.sort))

	return (
		<Suspense fallback={null}>
			<Section
				title={section.settings.title || collection?.title}
				section={section}
				backgroundColor={section.settings.background_color}
			>
				<List className={cn({ 'grid-cols-2': section.settings.size == 'small' })}>
					{products?.map(product => (
						<ProductCard.Container key={product.id} product={product} size={section.settings.size}>
							<ProductCard.Actions>
								<ProductCard.Action size="xs" href={productUrl(product)} className="sm:px-12">
									{t?.actions.buy_now}
								</ProductCard.Action>
								{section.settings.show_quick_add && (
									<ProductCard.Action
										size="xs"
										title={t?.actions.add_to_cart}
										onClick={_ =>
											dispatch(Actions.addToCart([{ quantity: 1, id: product.variants[0]?.id }]))
										}
									>
										+
									</ProductCard.Action>
								)}
							</ProductCard.Actions>
						</ProductCard.Container>
					))}
				</List>
			</Section>
		</Suspense>
	)
}

export const Collection: React.FC<CollectionProps> = ({ section }) => (
	<Suspense fallback={null}>
		<CollectionSection section={section} />
	</Suspense>
)
